/**
 * Pill-level fallback renames for raw backend status codes.
 *
 * Prefer `src/lib/labels.ts` in new code: it has one map per status family
 * (invite stage, payout, request, lead) because the same code means
 * different things in different families (PENDING is "Replied" on an invite
 * but "Queued" on a payout). Only codes that read the same in every family
 * belong here.
 *
 * Display labels for raw backend status codes.
 *
 * Override here to rename a status in the UI without touching every
 * callsite. Keys are the raw backend strings (the same ones the API
 * round-trips); values are the user-facing strings shown in pills, filters,
 * dialogs, and copy.
 *
 * Statuses NOT in this map fall through to the default Pill title-case
 * transform — `PENDING` → `Pending`, `AWAITING_RATE` → `Awaiting Rate`, etc.
 * Only put a code here when you want to *rename* it.
 *
 * One source of truth across surfaces: `Pill` reads this map directly, so
 * adding an entry here updates every status pill in the app simultaneously.
 *
 * Examples:
 *   VERIFIED  → "Joined"  (operator-friendly: "the inviter joined the program")
 *   CONVERTED → "Posted"  (operator-friendly: "they posted their first piece")
 */
export const STATUS_LABELS: Record<string, string> = {
  // Outreach invite vocabulary — VERIFIED/CONVERTED are accurate to the data
  // model but don't match the operator mental model. Rename for clarity.
  VERIFIED: 'Joined',
  CONVERTED: 'Posted',
};

/**
 * Resolve a raw status code to its display label. Returns the raw code
 * unchanged when no explicit mapping exists — the caller is responsible
 * for any title-casing fallback (the Pill primitive handles that natively).
 *
 * Use this directly when rendering raw status text outside a Pill, e.g.
 * in a tooltip or filter label.
 */
export function statusLabelFor(raw: string | null | undefined): string {
  if (!raw) return '';
  return STATUS_LABELS[raw] ?? raw;
}
